import { Select } from '~/components/ui/Form.tsx';
import { SelectProps } from '~/components/ui/forms/Select.tsx';
import { useMemo } from 'react';
import { timeOptions } from '~/utils/format.ts';

type TimeSelectProps = { value: string; intervalMinutes?: number } & Omit<
    SelectProps<string>,
    'options' | 'value' | 'filterOption'
>;

export function TimeSelect({ value, intervalMinutes = 15, ...props }: TimeSelectProps) {
    const OPTIONS = useMemo(() => timeOptions(intervalMinutes), [intervalMinutes]);
    return (
        <Select
            {...props}
            filterOption={customTimeFilter}
            value={OPTIONS.find((o) => o.value === value)}
            options={OPTIONS}
        />
    );
}

const hourRegex = /([0-9:]+)/;
const amPmRegex = /(a|am|p|pm)/;

export function customTimeFilter(option: { value: string }, input: string): boolean {
    if (!input.trim()) return true;
    const optAmPm = option.value.substring(option.value.length - 2);
    const timeParts = option.value.substring(0, option.value.length - 2).split(':');

    input = input.trim().replace(' ', '').toLowerCase();
    const ampm = amPmRegex.exec(input)?.pop?.();
    if (ampm && !optAmPm.startsWith(ampm)) return false;

    const inputParts = hourRegex.exec(input)!.pop()!.split(':');
    if (inputParts.length === 2) {
        return timeParts[0] === inputParts[0] && timeParts[1].startsWith(inputParts[1]);
    } else if (inputParts.length === 1) {
        const input = inputParts[0];
        if (input.length === 3) {
            // If we have 3 numbers two things can happen: it's HMM or HHM, so we try both.
            // HHM
            const h1 = input.substring(0, 2);
            const m1 = input.substring(2);
            // HMM
            const h2 = input.substring(0, 1);
            const m2 = input.substring(1);
            return (
                (h1 === timeParts[0] && timeParts[1].startsWith(m1)) ||
                (h2 === timeParts[0] && timeParts[1].startsWith(m2))
            );
        } else if (input.length === 2) {
            // If the number is <= 12 we treat it as an hour
            if (Number(input) <= 12) {
                return input === timeParts[0];
            }

            // Otherwise as HM
            const hour = input.substring(0, 1);
            const minute = input.substring(1);
            return hour === timeParts[0] && timeParts[1].startsWith(minute);
        } else if (input.length === 1) {
            return input === timeParts[0];
        } else {
            // We assume it's HHMM
            const hour = input.substring(0, 2);
            const minute = input.substring(2, 4);
            return hour === timeParts[0] && timeParts[1].startsWith(minute);
        }
    }

    return false;
}
